// JSON 데이터를 Google Sheets 형태로 변환하는 유틸리티

// 과목별 매핑 정보
const SUBJECT_MAPPING = {
  '1과목': {
    keywords: ['소프트웨어개발방법론', '화면설계', '화면구현', '요구사항', '인터페이스설계', '애플리케이션설계', '기능모델링', '정적모델', '동적모델'],
    modules: [
      'LM2001020219_애플리케이션요구사항분석',
      'LM2001020220_기능모델링', 
      'LM2001020221_애플리케이션설계',
      'LM2001020222_정적모델설계',
      'LM2001020223_동적모델설계',
      'LM2001020224_화면설계',
      'LM2001020225_화면구현',
      'LM2001020218_인터페이스설계',
      'LM2001020229_소프트웨어개발방법론활용'
    ]
  },
  '2과목': {
    keywords: ['프로그래밍', '데이터입출력', '통합구현', '제품소프트웨어패키징', '애플리케이션테스트', '인터페이스구현', '서버프로그램', '배치프로그램'],
    modules: [
      'LM2001020230_프로그래밍 언어 응용',
      'LM2001020231_프로그래밍 언어 활용',
      'LM2001020205_데이터입출력구현',
      'LM2001020206_통합구현',
      'LM2001020212_인터페이스구현',
      'LM2001020211_서버프로그램구현',
      'LM2001020226_애플리케이션테스트관리',
      'LM2001020227_애플리케이션테스트수행',
      'LM2001020209_제품소프트웨어패키징'
    ]
  },
  '3과목': {
    keywords: ['데이터베이스', '운영체제', '네트워크', '응용SW', '개발환경', '미들웨어'],
    modules: [
      'LM2001020232_응용SW 기초 기술 활용',
      'LM2001020233_개발자 환경 구축',
      'LM2001020234_개발 환경 운영 지원'
    ]
  },
  '4과목': {
    keywords: ['소프트웨어공학', '시스템이행', '배포', '리팩토링', 'CASE', '품질관리'],
    modules: [
      'LM2001020228_소프트웨어공학활용',
      'LM2001020208_정보시스템이행',
      'LM2001020214_애플리케이션배포',
      'LM2001020217_애플리케이션리팩토링'
    ]
  },
  '5과목': {
    keywords: ['정보보안', '요구사항확인', '현행시스템', '분석모델'],
    modules: [
      'LM2001020201_요구사항확인'
    ]
  }
};

// 모듈 이름을 과목으로 매핑
export const getSubjectByModule = (moduleId) => {
  for (const [subject, info] of Object.entries(SUBJECT_MAPPING)) {
    if (info.modules.includes(moduleId)) {
      return subject;
    }
  }
  return '기타';
};

// 모듈 이름을 한글 과목명으로 변환
export const getSubjectName = (subject) => {
  const subjectNames = {
    '1과목': '소프트웨어 설계',
    '2과목': '소프트웨어 개발',
    '3과목': '데이터베이스 구축',
    '4과목': '프로그래밍 언어 활용',
    '5과목': '정보시스템 구축관리'
  };
  return subjectNames[subject] || subject;
};

// JSON 청크 데이터를 Google Sheets 형태로 변환
export const convertChunksToSheetsData = (chunksData, modulesInfo) => {
  const sheetsData = [];

  chunksData.forEach(chunk => {
    const moduleInfo = modulesInfo[chunk.doc_id];
    const subject = getSubjectByModule(chunk.doc_id);
    const subjectName = getSubjectName(subject);
    
    // 키워드 추출 (간단한 버전)
    const keywords = extractKeywords(chunk.text, subject);
    
    // 내용 유형 추출
    const contentType = chunk.annotations?.type?.join(', ') || '';
    
    // 요약 생성 (간단한 버전)
    const summary = generateSummary(chunk.text);
    
    sheetsData.push({
      subjectCode: subject,
      subjectName: subjectName,
      lessonNum: chunk.lesson_num,
      lessonTitle: chunk.lesson_title,
      topicId: chunk.topic_id,
      topicTitle: chunk.topic_title,
      contentType: contentType,
      keywords: keywords.join(', '),
      summary: summary,
      details: chunk.text,
      chunkCount: 1,
      totalLength: chunk.length
    });
  });

  return sheetsData;
};

// 모듈 정보를 Google Sheets 형태로 변환
export const convertModulesToSheetsData = (modulesInfo) => {
  const sheetsData = [];

  Object.entries(modulesInfo).forEach(([moduleId, moduleData]) => {
    const subject = getSubjectByModule(moduleId);
    const subjectName = getSubjectName(subject);

    Object.entries(moduleData.lessons).forEach(([lessonNum, lessonData]) => {
      Object.entries(lessonData.topics).forEach(([topicId, topicTitle]) => {
        const keywords = extractKeywords(topicTitle, subject);
        
        sheetsData.push({
          subjectCode: subject,
          subjectName: subjectName,
          lessonNum: lessonNum,
          lessonTitle: lessonData.title,
          topicId: topicId,
          topicTitle: topicTitle.replace(/^\d+\s/, ''), // 앞의 숫자 제거
          contentType: '개요',
          keywords: keywords.join(', '),
          summary: `${lessonData.title}의 ${topicTitle} 내용`,
          details: `${moduleData.name} - ${lessonData.title}의 세부 주제: ${topicTitle}`,
          chunkCount: 0,
          totalLength: 0
        });
      });
    });
  });

  return sheetsData;
};

// 키워드 추출 함수
const extractKeywords = (text, subject) => {
  const subjectKeywords = SUBJECT_MAPPING[subject]?.keywords || [];
  const extractedKeywords = [];

  // 과목별 키워드 매칭
  subjectKeywords.forEach(keyword => {
    if (text.includes(keyword)) {
      extractedKeywords.push(keyword);
    }
  });

  // 일반적인 기술 용어 추출
  const techTerms = text.match(/[가-힣]{2,}(?:시스템|모델|방법|기법|분석|설계|구현|테스트|관리|요구사항|인터페이스|아키텍처|소프트웨어|하드웨어|네트워크|데이터베이스|프로그래밍|알고리즘)/g) || [];
  
  // 중복 제거 및 최대 5개까지
  const uniqueKeywords = [...new Set([...extractedKeywords, ...techTerms])];
  return uniqueKeywords.slice(0, 5);
};

// 간단한 요약 생성 함수
const generateSummary = (text) => {
  if (text.length <= 100) return text;
  
  // 첫 번째 문장이나 첫 100자 반환
  const firstSentence = text.split('.')[0];
  if (firstSentence.length <= 100) {
    return firstSentence + '.';
  }
  
  return text.substring(0, 97) + '...';
};

// 과목별로 데이터 그룹화
export const groupDataBySubject = (sheetsData) => {
  const grouped = {};
  
  sheetsData.forEach(item => {
    if (!grouped[item.subjectCode]) {
      grouped[item.subjectCode] = [];
    }
    grouped[item.subjectCode].push(item);
  });
  
  return grouped;
};

// 학습 단위별로 데이터 그룹화
export const groupDataByLesson = (sheetsData) => {
  const grouped = {};
  
  sheetsData.forEach(item => {
    const key = `${item.subjectCode}-${item.lessonNum}`;
    if (!grouped[key]) {
      grouped[key] = {
        subjectCode: item.subjectCode,
        subjectName: item.subjectName,
        lessonNum: item.lessonNum,
        lessonTitle: item.lessonTitle,
        topics: []
      };
    }
    grouped[key].topics.push(item);
  });
  
  return Object.values(grouped);
};

// 데이터 검증 및 정제
export const validateAndCleanData = (sheetsData) => {
  return sheetsData.filter(item => {
    // 필수 필드 체크
    return item.subjectCode && 
           item.lessonNum && 
           item.topicId && 
           item.topicTitle &&
           item.topicTitle.trim().length > 0;
  }).map(item => ({
    ...item,
    // 텍스트 정제
    summary: item.summary?.replace(/\s+/g, ' ').trim() || '',
    details: item.details?.replace(/\s+/g, ' ').trim() || '',
    keywords: item.keywords?.replace(/\s+/g, ' ').trim() || '',
    topicTitle: item.topicTitle?.trim() || '',
    lessonTitle: item.lessonTitle?.trim() || ''
  }));
};

export { SUBJECT_MAPPING };